import * as React from 'react';
import { useCallback, useState } from 'react';
import type { DragEvent } from 'react';

interface UseDropzoneParams {
  onFilesSelected: (files: Array<File>) => void;
  isDisabled?: boolean;
  acceptedFileTypes: Array<string>;
  maxFiles: number;
  currentFilesCount: number;
}

export function useDropzone({
  onFilesSelected,
  isDisabled = false,
  acceptedFileTypes,
  maxFiles,
  currentFilesCount,
}: UseDropzoneParams) {
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (isDisabled) return;
      // For multiple files mode, block if at capacity
      // For single file mode, always allow (will replace existing file)
      if (maxFiles > 1 && currentFilesCount >= maxFiles) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFilesSelected(maxFiles > 1 ? files : [files[0]]);
      }
    },
    [isDisabled, maxFiles, currentFilesCount, onFilesSelected]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (isDisabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    },
    [isDisabled]
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (isDisabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
    },
    [isDisabled]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;
      // For multiple files mode, block if at capacity
      // For single file mode, always allow (will replace existing file)
      if (maxFiles > 1 && currentFilesCount >= maxFiles) return;
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        onFilesSelected(files);
      }
      e.target.value = '';
    },
    [isDisabled, maxFiles, currentFilesCount, onFilesSelected]
  );

  return {
    isDragActive,
    getRootProps: () => ({
      onDrop: handleDrop,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
    }),
    getInputProps: () => ({
      type: 'file' as const,
      accept: acceptedFileTypes.join(','),
      onChange: handleFileInput,
      multiple: maxFiles > 1 ? true : false,
      style: { display: 'none' },
    }),
  };
}
